import { ComponentType, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Box, Button, Card, Display, HStack, Image, Spinner, Stack, Tag, Text, VStack } from '@bonusly/design-system';
import { CalendarOutline, ClockOutline, CoinPointsOutline, IconProps } from '@bonusly/ds-icon';

import { AdminPageContainer } from '../../../components/layout/admin-page-container';
import { PageTitle } from '../../../components/page/page-title';
import { usePointExpiryPeriod, usePointExpiryPeriods } from '../../../queries/point-expiry';
import { PointExpiryPeriod } from '../../../schemas/point-expiry';
import { getDateFnsLocale } from '../../../utils/date';
import joseTheAccountant from '../assets/jose-the-accountant.png';
import { formatPeriodDate, POINT_EXPIRY_INDEX_PATH, pointExpiryEditPath } from '../point-expiry-form-helpers';

// One labeled fact inside the live-period card: an icon, a soft uppercase label,
// and the value. Keeps the three facts (window / reminder / impact) visually aligned.
type DetailRowProps = {
	icon: ComponentType<IconProps>;
	label: string;
	children: ReactNode;
};

const DetailRow: FC<DetailRowProps> = ({ icon: Icon, label, children }) => (
	<HStack align="flex-start" spacing={3}>
		<Box color="text.softer" pt="2px">
			<Icon size="sm" color="text.softer" />
		</Box>
		<VStack align="flex-start" spacing={0}>
			<Text fontSize={50} color="text.softer" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
				{label}
			</Text>
			<Box>{children}</Box>
		</VStack>
	</HStack>
);

// The impact line needs the SHOW payload (the list endpoint omits the count), so
// the live period's detail is fetched separately here.
const ImpactLine: FC<{ periodId: string }> = ({ periodId }) => {
	const { t, i18n } = useTranslation('admin', { keyPrefix: 'pointExpiryPeriods' });
	const dateLocale = getDateFnsLocale(i18n.language);
	const { data: detail, isLoading } = usePointExpiryPeriod(periodId);

	if (isLoading) {
		return (
			<HStack spacing={2} color="text.soft">
				<Spinner size="xs" />
				<Text fontSize={100}>{t('activeCard.impactLoading')}</Text>
			</HStack>
		);
	}

	if (detail?.impactedPointsCount === undefined) {
		return (
			<Text fontSize={100} color="text.soft">
				{t('activeCard.impactUnavailable')}
			</Text>
		);
	}

	return (
		<Text fontSize={100} fontWeight="semibold" data-testid="impacted-points-count">
			{t('activeCard.impact', { impactedCount: detail.impactedPointsCount, date: formatPeriodDate(detail.endAt, dateLocale) })}
		</Text>
	);
};

const LivePeriodCard: FC<{ period: PointExpiryPeriod }> = ({ period }) => {
	const { t, i18n } = useTranslation('admin', { keyPrefix: 'pointExpiryPeriods' });
	const dateLocale = getDateFnsLocale(i18n.language);

	const windowText = period.startAt
		? t('activeCard.window', { start: formatPeriodDate(period.startAt, dateLocale), end: formatPeriodDate(period.endAt, dateLocale) })
		: t('activeCard.windowNoStart', { end: formatPeriodDate(period.endAt, dateLocale) });

	return (
		<Card p={6} data-testid="live-period-card">
			<Stack spacing={5}>
				<HStack align="center">
					<Tag size="lg" colorScheme={period.status === 'active' ? 'green' : 'blue'} variant="subtle" borderRadius="full">
						{t(`status.${period.status}`)}
					</Tag>
					<Box flex={1} />
					<Button as={Link} to={pointExpiryEditPath(period.id)} variant="outline">
						{t('editCta')}
					</Button>
				</HStack>

				<Stack spacing={4}>
					<DetailRow icon={CalendarOutline} label={t('activeCard.windowLabel')}>
						<Text fontSize={100} fontWeight="semibold">
							{windowText}
						</Text>
					</DetailRow>

					<DetailRow icon={ClockOutline} label={t('activeCard.warningLabel')}>
						<Text fontSize={100}>{t('activeCard.warning', { date: formatPeriodDate(period.warningAt, dateLocale) })}</Text>
					</DetailRow>

					<DetailRow icon={CoinPointsOutline} label={t('activeCard.impactLabel')}>
						<ImpactLine periodId={period.id} />
					</DetailRow>
				</Stack>
			</Stack>
		</Card>
	);
};

const EmptyState: FC = () => {
	const { t } = useTranslation('admin', { keyPrefix: 'pointExpiryPeriods' });

	return (
		<Card p={6} data-testid="empty-state">
			<VStack spacing={4} align="center" textAlign="center" maxW="lg" mx="auto">
				<Display size="xs">{t('emptyState.title')}</Display>
				<Text color="text.soft">{t('emptyState.body')}</Text>
				<Button as={Link} to={`${POINT_EXPIRY_INDEX_PATH}/new`} colorScheme="primary">
					{t('createCta')}
				</Button>
			</VStack>
		</Card>
	);
};

export const PointExpiryPage: FC = () => {
	const { t } = useTranslation('admin', { keyPrefix: 'pointExpiryPeriods' });
	const { data: periods = [] } = usePointExpiryPeriods();

	// Single-live lifecycle: at most one upcoming|active period exists at a time.
	// Completed/deleted periods are intentionally not surfaced — no history list.
	const live = periods.find((period) => period.status === 'upcoming' || period.status === 'active');

	return (
		<AdminPageContainer width="full">
			<Stack spacing={6} maxW="2xl">
				<PageTitle>{t('title')}</PageTitle>

				<Stack spacing={2}>
					<Display size="sm">{t('title')}</Display>
					<Text fontSize={100} color="text.soft">
						{t('description')}
					</Text>
				</Stack>

				<Box>
					<Image src={joseTheAccountant} alt={t('mascotAlt')} maxW="280px" height="auto" mx="auto" mb={-6} data-testid="point-expiry-mascot" />
				</Box>

				{live ? <LivePeriodCard period={live} /> : <EmptyState />}
			</Stack>
		</AdminPageContainer>
	);
};
